/** @format */

import React, { useState, useLayoutEffect, useRef } from "react";
import {
  cocoaSymbolToMonth,
  dataUrItoBlob,
  getCocoaPercentChanges,
  uploadJPEG,
  getMonth,
  getPrice,
  getCocoaPrice,
  getFormattedDateForSyndication,
  formatPriceInUS,
} from "../../utils/utils";
import { fileUpload } from "../../utils/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { toJpeg } from "html-to-image";
import moment from "moment";
import useSavePreviousSession from "./hooks/useSavePreviousSession";
import { Messages } from "primereact/messages";
import ThreeDotsLoader from "../common/ThreeDotsLoader";
import service from "../../utils/service";
import { api_key, C3_api_key } from "../../utils/common";
import { isPreviousEmpty } from "./utils/utils";
import { BUSINESS_UNIT } from "../../utils/PreviousSession.constants";
import {
  SOURCE,
  REGIONS,
  FIELDS,
  prev_sess_chart_symbols,
  prev_sess_line_indexes,
  GENERATE_IMAGE_BUSINESS_UNITS,
} from "./constants/constants";
import "../../style/syndication.css";
import { TRANSLATIONS } from "../../constants/translations.constants";

function PriceTable(props) {
  let isFooterNotAvailable = props.footerData.length === 0;
  let hideFooterStyle = isFooterNotAvailable ? { display: "none" } : {};
  const isEdibleOils = props.businessUnit.toLocaleLowerCase() === BUSINESS_UNIT.EDIBLE_OILS.toLocaleLowerCase();
  const isCocoa = props.businessUnit.toLocaleLowerCase() === BUSINESS_UNIT.COCOA.toLocaleLowerCase();
  const isCorn = props.businessUnit.toLocaleLowerCase() === BUSINESS_UNIT.CORN.toLocaleLowerCase();

  // Get the translations for the region
  const { region } = props;
  const referenceMarketText = TRANSLATIONS[region]["Reference Market"];
  const contractMonthText = TRANSLATIONS[region]["Contract Month"];
  const settlementPriceText = TRANSLATIONS[region]["Settlement Price"];
  const weeklyPercentageChange = TRANSLATIONS[region]["Weekly % Changes"];
  const fundPositionText = TRANSLATIONS[region]["Fund Position"];
  const netContractsText = TRANSLATIONS[region]["Net Contracts"];
  const weeklyChangeText = TRANSLATIONS[region]["Weekly Change"];
  const previousWeekMarketCloseText = TRANSLATIONS[region]["Previous Week Market close"];
  const previousMarketCloseText = TRANSLATIONS[region]["Previous Market close"];
  const percentageChange = TRANSLATIONS[region]["% Change"];
  const lastCloseDateText = TRANSLATIONS[region]["Last Close Date"];
  const previousCloseDateText = TRANSLATIONS[region]["Previous Close Date"];
  const sourceText = TRANSLATIONS[region]["Source"];

  const percentChangeText = isEdibleOils
    ? props.region === REGIONS.NA_MX_ES
      ? TRANSLATIONS[region]["Weekly % Changes"]
      : "Daily % Change"
    : isCorn
      ? percentageChange
      : weeklyPercentageChange;

  let hdrStyle = {
    background: isCocoa ? "#be621c" : "#199b8b",
    color: "white",
    border: "none",
  };

  const HEADER_COLUMNS = [
    { header: referenceMarketText, field: "market", style: { ...hdrStyle, fontWeight: "400" } },
    {
      header: contractMonthText,
      field: "month",
      align: "center",
      style: { ...hdrStyle, textAlign: "center", fontWeight: "400" },
    },
    {
      header: settlementPriceText,
      field: "price",
      align: "center",
      style: { ...hdrStyle, textAlign: "center", fontWeight: "400" },
    },
    {
      header: percentChangeText,
      field: "percentChange",
      align: "center",
      style: { ...hdrStyle, textAlign: "center", fontWeight: "400" },
    },
  ];
  if (props.region !== REGIONS.NA && props.region !== REGIONS.NA_MX_ES) {
    HEADER_COLUMNS.splice(1, 0, { header: "CCY", field: "currency", style: { ...hdrStyle, fontWeight: "400" } });
  }
  let headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header={isCorn ? previousMarketCloseText : previousWeekMarketCloseText}
          colSpan={HEADER_COLUMNS.length}
          align={"center"}
          style={{ ...hdrStyle, textAlign: "center", fontSize: "0.8rem" }}
        />
      </Row>
      <Row>
        {HEADER_COLUMNS.map((col) => (
          <Column key={col.field} {...col} className={col.className} />
        ))}
      </Row>
    </ColumnGroup>
  );

  let footerHeaderGroup = (
    <ColumnGroup>
      <Row>
        <Column header={fundPositionText} style={{ ...hdrStyle, fontWeight: "400", ...hideFooterStyle }} />
        <Column header={netContractsText} style={{ ...hdrStyle, fontWeight: "400", ...hideFooterStyle }} />
        <Column header={weeklyChangeText} style={{ ...hdrStyle, fontWeight: "400", ...hideFooterStyle }} />
      </Row>
    </ColumnGroup>
  );

  let changeColTemplate = (rowData) => {
    var val;
    var symbol;
    if (rowData.percentChange !== undefined) {
      symbol = "%";
      val = rowData.percentChange;
    } else {
      symbol = "";
      val = rowData.change;
    }

    const base_style = { fontWeight: 450, padding: "2px 4px", textAlign: "center" };
    return (
      <div
        style={
          val < 0
            ? { background: "#ffb3b3", color: "#000", borderBottom: "2px solid #ffb3b3", ...base_style }
            : val > 0
              ? { background: "#88ddd3", color: "#000", borderBottom: "2px solid #88ddd3", ...base_style }
              : { background: "white", ...base_style, color: "#000" }
        }
      >
        {Math.floor(val * 100) / 100}
        {symbol}
      </div>
    );
  };

  let colTemplate = (rowData, field) => {
    const priceTruncation = ["USD/JPY", "USD/KRW"];
    let price = null;
    if (field.field === FIELDS.price) {
      price = rowData[field.field];
      if (isCocoa && priceTruncation.includes(rowData.market)) {
        price = Math.floor(price);
      }
    }
    return (
      <div
        style={
          props.line_indexes.includes(field.rowIndex)
            ? { borderBottom: "1.2px solid black", padding: "2px 4px" }
            : { padding: "2px 4px" }
        }
      >
        {field.field === FIELDS.price ? (
          price
        ) : field.field === FIELDS.market && isCocoa ? (
          <span>
            {rowData[field.field]}
            <sup style={{ fontSize: "0.3rem" }}>{rowData["source"]}</sup>
          </span>
        ) : field.field === FIELDS.volume ? (
          new Intl.NumberFormat("en-US").format(rowData[field.field])
        ) : (
          rowData[field.field]
        )}
      </div>
    );
  };
  const { businessUnit } = props;
  const closeDate = getFormattedDateForSyndication(businessUnit, region, props.closeDate);
  const previousCloseDate = getFormattedDateForSyndication(businessUnit, region, props.previousCloseDate);
  var footerText = "";
  if (isCocoa) {
    footerText = `(${sourceText}: 1-CRA, 2-Barchart)\n(${lastCloseDateText}: ${closeDate})`;
  } else {
    footerText = `${sourceText}: Barchart.com`;
  }
  const BODY_COLUMNS = [
    {
      field: "market",
      body: colTemplate,
      align: "left",
      style: { color: "black", fontWeight: "400", textAlign: "left", fontSize: "8px" },
    },
    {
      field: "month",
      body: colTemplate,
      align: "center",
      style: { color: "black", fontWeight: "400", textAlign: "center", fontSize: "8px" },
    },
    {
      field: "price",
      body: colTemplate,
      align: "center",
      style: { color: "black", fontWeight: "bold", textAlign: "center", fontSize: "8px" },
    },
    { field: "percentChange", body: changeColTemplate, fontSize: "8px" },
  ];
  if (props.region !== REGIONS.NA && props.region !== REGIONS.NA_MX_ES) {
    BODY_COLUMNS.splice(1, 0, {
      field: "currency",
      body: colTemplate,
      align: "left",
      style: { color: "black", fontWeight: "400", textAlign: "left" },
    });
  }
  return (
    <div id='barTableDiv'>
      <DataTable
        size='small'
        value={props.data}
        headerColumnGroup={headerGroup}
        className='p-datatable-striped'
        pt={{ table: { style: { borderCollapse: "collapse" } } }}
      >
        {BODY_COLUMNS.map((col) => (
          <Column key={col.field} {...col} className={col.className} />
        ))}
      </DataTable>
      <DataTable
        size='small'
        emptyMessage=' '
        value={props.footerData}
        headerColumnGroup={footerHeaderGroup}
        className='p-datatable-striped'
        footer={footerText}
        pt={{
          emptyMessage: { style: { display: "none" } },
          table: { style: { borderCollapse: "collapse", width: "100%" } },
        }}
      >
        <Column
          field='market'
          body={colTemplate}
          style={{ color: "black", fontWeight: "400", textAlign: "left", ...hideFooterStyle }}
        />
        <Column field='volume' body={colTemplate} style={{ color: "black", textAlign: "center", ...hideFooterStyle }} />
        <Column field='change' body={changeColTemplate} style={{ ...hideFooterStyle }} />
      </DataTable>
    </div>
  );
}
function PreviousSession({
  publish,
  businessUnit,
  closeDate,
  previousCloseDate,
  regions,
  region,
  selectedDate,
  diff_date,
  getSectionData,
  section,
  updateImagePath,
  getContentBasedonDateAndRegion,
  loading,
  productLineId,
  setEditing,
  updateSectionId,
  ...rest
}) {
  const [editContent, setEditContent] = useState(false);
  const [temp, setTemp] = useState("");
  const [isSectionInValid, setIsSectionInValid] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [barchartData, setbarchartdata] = useState([]);
  const [craReportDate, setCraReportDate] = useState("");
  const [barchartReportDate, setBarchartReportDate] = useState("");
  const [barchartFooterData, setBarchartFooterData] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [line_indexes, setLineIndexes] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [uploadPercent, setUploadPercent] = useState(0);
  const mutation = useSavePreviousSession();
  const msgRef = useRef();
  const dataTableWidthRef = useRef(0);

  useLayoutEffect(() => {
    let callback = (records, observer) => {
      var node = document.getElementById("barTableDiv");
      if (!node) return false;
      document.querySelector("html").classList.add("hide-scrollbar");
      dataTableWidthRef.current = node.clientWidth;
      toJpeg(node, {
        width: node.clientWidth,
        height: node.clientHeight,
        quality: 0.95,
        pixelRatio: window.devicePixelRatio !== 2 ? window.devicePixelRatio * 2 : window.devicePixelRatio,
      }).then((dataUrl) => {
        let __blob = dataUrItoBlob(dataUrl);
        uploadJPEG(
          __blob,
          section,
          businessUnit + region + moment(selectedDate).format("YYYYMMDD") + "barchart.png"
        ).then((imagePath) => {
          updateImagePath(section, imagePath, "");
        });
      });
      document.querySelector("html").classList.remove("hide-scrollbar");
    };

    let observer = new MutationObserver(callback);
    let wrapper = document.getElementById("barTable__wrapper");
    const observerOptions = {
      childList: true,
      subtree: true,
    };
    if (wrapper) {
      observer.observe(wrapper, observerOptions);
    }
    return () => observer.disconnect();
  }, [section, businessUnit, region, selectedDate, updateImagePath]);

  function handleEdit(section) {
    setTemp(section.content);
    setEditContent(true);
    setEditing("previousSession", true, false);
  }

  function handleCancel() {
    setEditContent(false);
  }

  function recurse_request(i, intervals, symbols, names, currency, showFooter, data) {
    if (i === intervals.length - 1) {
      setbarchartdata(data);
      setShowImage(true);
      setDisableButton(false);
    } else {
      var data_json = {};
      if (!isNaN(parseInt(symbols[intervals[i]]))) {
        data_json = symbols[intervals[i]];
        const regionId = data_json;
        const priceTruncation = ["USA CCPOWDER", "Asia CCPOWDER"];
        service.getlatestCraData(data_json).then((resp) => {
          let result_arr = [];
          const response = resp.data;
          for (const commodity of response) {
            const commoditySymbol = commodity.commoditySymbol;
            if (commoditySymbol.includes("CCBUTTER")) {
              result_arr[0] = commodity;
            } else if (
              (regionId.toString().includes("2") || regionId.toString().includes("5")) &&
              commoditySymbol.includes("CCLIQUOR")
            ) {
              result_arr[1] = commodity;
            }
          }
          const powderCommodity = response.find((commodity) => commodity.commoditySymbol.includes("CCPOWDER"));
          if (powderCommodity) {
            result_arr.push(powderCommodity);
          }

          var temp_data = result_arr.map((item, index) => {
            setCraReportDate(moment(item.reportDate).format("YYYY/MM/DD"));
            let craData = {
              market: names[1] === "" ? item.name : names[intervals[i] + index],
              currency: currency.length !== 0 ? currency[intervals[i] + index] : "",
              source: SOURCE.SOURCE_1,
              month: cocoaSymbolToMonth(item.contractDeliveryMonth.substr(0, 1)) + item.contractDeliveryMonth.substr(1),
              price: item.commodityPrice,
              percentChange: item.percentageChange,
            };
            if (priceTruncation.includes(craData.market)) {
              craData.price = Math.floor(craData.price);
            }
            return craData;
          });
          if (showFooter && i === intervals.length - 2) {
            setBarchartFooterData(temp_data);
          } else {
            data = data.concat(temp_data);
          }
          recurse_request(i + 1, intervals, symbols, names, currency, showFooter, data);
        });
      } else {
        if (symbols[intervals[i]].startsWith("C3:")) {
          data_json["apikey"] = C3_api_key;
        } else {
          data_json["apikey"] = api_key;
        }

        // added previousPercentChange
        data_json["fields"] =
          "month,commodityDataCurrency,exchange,commodityDataDelivery,volume,lastTradingDay,tradeDate,weeklyClose,weeklyPreviousClose,previousSettlement,previousPercentChange,lastPrice,previousLastPrice,close,previousClose";
        var loc_symbols = symbols.slice(intervals[i], intervals[i + 1]);
        data_json["symbols"] = loc_symbols.join(",");

        var isFooter = showFooter && i === intervals.length - 2;

        service.getChartData(data_json).then((resp) => {
          var temp_data = resp.results.map((item, index) => {
            if (isFooter) {
              return {
                market: names[intervals[i] + index],
                volume: item.high,
                change: item.netChange,
                source: SOURCE.SOURCE_2,
              };
            }
            setBarchartReportDate(moment(item.tradeDate).format("YYYY/MM/DD"));
            const market = names[1] === "" ? item.name : names[intervals[i] + index];
            let chartData = {
              market,
              currency: currency.length !== 0 ? currency[intervals[i] + index] : "",
              source: SOURCE.SOURCE_2,
              month: getMonth(item),
              price: formatPriceInUS(getPrice(item, region)),
              percentChange: item.previousPercentChange,
            };

            return chartData;
          });
          if (isFooter) {
            setBarchartFooterData(temp_data);
            recurse_request(i + 1, intervals, symbols, names, currency, showFooter, data);
          } else {
            if (businessUnit === BUSINESS_UNIT.COCOA) {
              getCocoaPercentChanges(loc_symbols, closeDate, previousCloseDate).then((res) => {
                for (var j = 0; j < res.length; j++) {
                  const comm = res[j];
                  temp_data[j].price = getCocoaPrice(comm);
                  temp_data[j].percentChange = comm.percentChange;
                }
                data = data.concat(temp_data);
                recurse_request(i + 1, intervals, symbols, names, currency, showFooter, data);
              });
            } else {
              data = data.concat(temp_data);
              recurse_request(i + 1, intervals, symbols, names, currency, showFooter, data);
            }
          }
        });
      }
    }
  }

  const uploadProgressHandler = (total, loaded) => {
    const percent = Math.round((100 * loaded) / total);
    setUploadPercent(percent);
  };
  const handleImageUpload = (e) => {
    setUploadPercent(1);
    fileUpload(e.files[0], section, uploadProgressHandler)
      .then((imagePath) => {
        updateImagePath(section, imagePath, "");
        setEditing("previousSession", true, false);
      })
      .catch((err) => setUploadPercent(0));
  };

  function handleSave(section) {
    section.content = temp;
    setEditContent(false);

    getSectionData(section);
  }

  function renderEditContent(publish, diff_date) {
    let returnObj =
      publish === true || diff_date < 0 ? null : (
        <tr className='editContent'>
          <td>
            <span
              style={{ fontSize: "16px", color: "grey" }}
              onClick={() => {
                handleEdit(section);
              }}
            >
              Edit Content
            </span>
          </td>
        </tr>
      );
    return returnObj;
  }

  function renderContent(editContent) {
    let returnObj = (
      <div>
        <div
          style={{
            fontSize: "14px",
            // color: "#cccccc",
            margin: "8px",
            whiteSpace: "pre-line",
            textAlign: "justify",
            wordBreak: "break-word",
          }}
        >
          {section.content}
        </div>
      </div>
    );
    return returnObj;
  }

  // const [headerImgSrc, setHeaderImgSrc] = React.useState("")

  const headerImgSrc =
    businessUnit !== BUSINESS_UNIT.COCOA
      ? region === REGIONS.SA
        ? "https://miappstorage.dev.cglcloud.in/api/miappstorage/v1/downloadImage/PreviousWeeksHighlights.png"
        : "https://miappstorage.dev.cglcloud.in/api/miappstorage/v1/downloadImage/PreviousSessionHighlights.png"
      : "https://miappstorage.dev.cglcloud.in/api/miappstorage/v1/downloadImage/cocoa_previous_session_highlights.png";

  React.useEffect(() => {
    if (businessUnit !== undefined && businessUnit !== "" && region !== undefined) {
      let PREV_SESS_CHART = prev_sess_chart_symbols[businessUnit];
      let PREV_SESS_REGION = PREV_SESS_CHART && PREV_SESS_CHART[region];
      let PREV_SESS_LINE = prev_sess_line_indexes[businessUnit];
      let PREV_SESS_LINE_REGION = PREV_SESS_LINE && PREV_SESS_LINE[region];
      if (PREV_SESS_REGION && PREV_SESS_LINE_REGION) {
        setSymbols(() => PREV_SESS_REGION);
        setLineIndexes(PREV_SESS_LINE_REGION);
      }
    }
    setShowImage(false);
    setBarchartFooterData([]);
  }, [region, businessUnit]);

  function handleClick() {
    setDisableButton(true);
    recurse_request(0, symbols.intervals, symbols.symbols, symbols.names, symbols.currency, symbols.footer, []);
    setEditing("previousSession", true, false);
  }

  const BUSINESS_UNITS = Object.keys(GENERATE_IMAGE_BUSINESS_UNITS);
  const showGenerateImage =
    symbols.length !== 0 &&
    BUSINESS_UNITS.includes(businessUnit) &&
    GENERATE_IMAGE_BUSINESS_UNITS[businessUnit].includes(region);

  function handleSectionSave() {
    if (isPreviousEmpty(section, productLineId)) {
      setIsSectionInValid(true);
    } else {
      mutation.mutate(
        {
          ...section,
          reportDate: rest.reportDate,
          regionId: rest.regionId,
          productLineId: productLineId,
        },
        {
          onSuccess: (data) => {
            if (msgRef.current) {
              msgRef.current.clear();
              msgRef.current.show([
                {
                  sticky: false,
                  severity: "success",
                  summary: "Success",
                  detail: "Previous Session saved successfully",
                  closable: false,
                  life: 2000,
                },
              ]);
            }
            setEditing("previousSession", false, false);
            updateSectionId("previous", data.data);
            mutation.reset();
          },
          onError: () => {
            if (msgRef.current) {
              msgRef.current.clear();
              msgRef.current.show([
                {
                  sticky: true,
                  severity: "error",
                  summary: "Error",
                  detail: "Error saving Previous Session",
                  closable: true,
                },
              ]);
            }
            setEditing("previousSession", true, false);
            mutation.reset();
          },
        }
      );
    }
  }
  return (
    <div className='relative syndication__section__wrapper'>
      {businessUnit !== BUSINESS_UNIT.GRAINS && (
        <>
          <table
            id='mainTable'
            align='center'
            style={{
              width: "800px",
              textAlign: "justify",
              borderSpacing: "5px",
              borderCollapse: "separate",
              tableLayout: "'fixed'",
              fontFamily: "Helvetica, Arial, sans-serif",
            }}
          >
            <tbody>
              <tr>
                <td>
                  <img
                    src={section?.headerImagePath || headerImgSrc}
                    width='800'
                    height='50'
                    alt='Previous Session Header Img'
                    style={{
                      marginLeft: "auto",
                      marginRight: "auto",
                      display: "block",
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {(loading || mutation.isLoading) && (
            <div className='wtg__loader absolute'>
              <ThreeDotsLoader className='absolute' />
            </div>
          )}
          <table width='800' align='center' className='msg__container'>
            <Messages
              className='msg__content'
              ref={msgRef}
              onRemove={() => {
                console.log("REMOVED");
              }}
            />
          </table>
          <table
            align='center'
            style={{
              width: "800px",
              textAlign: "justify",
              borderSpacing: "5px",
              borderCollapse: "separate",
              tableLayout: "'fixed'",
              fontFamily: "Helvetica, Arial, sans-serif",
              marginBottom: "10px",
            }}
          >
            <tbody>
              <tr>
                <td valign='top'>
                  {renderEditContent(publish, diff_date)}
                  {renderContent(editContent)}
                </td>
                <td valign='top' align='center' style={{ width: "30%" }} id='barTable__wrapper'>
                  {
                    // showImage
                    section.imagePath !== "" ? (
                      <div>
                        <img
                          width={`${dataTableWidthRef.current === 0 ? 230 : dataTableWidthRef.current}`}
                          alt='Previous Session Img'
                          style={{
                            backgroundColor: "white",
                          }}
                          src={section.imagePath}
                          onLoad={() => {
                            setIsImageLoading(false);
                          }}
                        />
                        {isImageLoading && (
                          <React.Fragment>
                            <FileUpload
                              className='fileUpload mb-2 floatRight generateImage mx-0'
                              customUpload
                              chooseLabel='Choose File'
                              mode='basic'
                              auto
                              pt={{
                                chooseIcon: "hidden",
                              }}
                              disabled={true}
                            />
                            {showGenerateImage && (
                              <Button
                                className='p-button p-button-sm diag-save-button floatRight generateImage mb-2'
                                disabled={true}
                                icon={"pi pi-spin pi-spinner"}
                                iconPos='right'
                                label='Generate Image'
                              />
                            )}
                            {showGenerateImage && (
                              <Button
                                pt={{ label: { className: "mx-0" } }}
                                className='p-button p-button-sm diag-save-button savesectionbtn'
                                label='Save Section Content & Image'
                                disabled={true}
                              />
                            )}
                          </React.Fragment>
                        )}
                        {publish === false && diff_date >= 0 && !isImageLoading && (
                          <>
                            <button
                              className='RemoveImage mx-0 p-button p-button-sm diag-save-button floatRight'
                              style={{
                                borderRadius: "35px",
                                margin: "8px",
                                textAlign: "center",
                                padding: "10px",
                                color: "#FFFFFF",
                                boxSizing: "border-box",
                                backgroundColor: "#199B8B",
                              }}
                              onClick={() => {
                                setShowImage(false);
                                setBarchartFooterData([]);
                                setUploadPercent(0);
                                updateImagePath(section, "", "");
                                setEditing("previousSession", true, false);
                              }}
                            >
                              Remove image
                            </button>
                            <Button
                              pt={{ label: { className: "mx-0" } }}
                              className='p-button p-button-sm diag-save-button savesectionbtn floatRight ml-0'
                              label='Save Section Content & Image'
                              onClick={() => handleSectionSave()}
                            />
                          </>
                        )}
                      </div>
                    ) : !showImage ? (
                      diff_date >= 0 && (
                        <React.Fragment>
                          <FileUpload
                            className='fileUpload mb-2 floatRight generateImage mx-0'
                            customUpload
                            uploadHandler={handleImageUpload}
                            chooseLabel='Choose File'
                            mode='basic'
                            auto
                            pt={{
                              chooseIcon: "hidden",
                            }}
                            disabled={disableButton || ![0, 100].includes(uploadPercent)}
                          />
                          {showGenerateImage && (
                            <Button
                              className='p-button p-button-sm diag-save-button floatRight generateImage mb-2'
                              disabled={disableButton || ![0, 100].includes(uploadPercent) ? true : false}
                              icon={disableButton ? "pi pi-spin pi-spinner" : null}
                              iconPos='right'
                              label='Generate Image'
                              onClick={() => handleClick()}
                            />
                          )}
                          {showGenerateImage && (
                            <Button
                              pt={{ label: { className: "mx-0" } }}
                              className='p-button p-button-sm diag-save-button savesectionbtn'
                              label='Save Section Content & Image'
                              onClick={() => handleSectionSave()}
                              disabled={disableButton || ![0, 100].includes(uploadPercent)}
                            />
                          )}
                        </React.Fragment>
                      )
                    ) : (
                      <>
                        <div style={{ width: "800px", height: "300px", position: "absolute", left: -9999999 }}>
                          <table>
                            <tr>
                              <td></td>
                              <td valign='top' align='center' style={{ width: "30%" }}>
                                <div className='barTable'>
                                  <PriceTable
                                    data={barchartData}
                                    closeDate={closeDate}
                                    previousCloseDate={previousCloseDate}
                                    footerData={barchartFooterData}
                                    region={region}
                                    line_indexes={line_indexes}
                                    businessUnit={businessUnit}
                                    footerDates={{
                                      craDate: craReportDate,
                                      barchartDate: barchartReportDate,
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                          </table>
                        </div>
                        <React.Fragment>
                          <FileUpload
                            className='fileUpload mb-2 floatRight generateImage mx-0'
                            customUpload
                            chooseLabel='Choose File'
                            mode='basic'
                            auto
                            pt={{
                              chooseIcon: "hidden",
                            }}
                            disabled={true}
                          />
                          {showGenerateImage && (
                            <Button
                              className='p-button p-button-sm diag-save-button floatRight generateImage mb-2'
                              disabled={true}
                              icon={"pi pi-spin pi-spinner"}
                              iconPos='right'
                              label='Generate Image'
                            />
                          )}
                          {showGenerateImage && (
                            <Button
                              pt={{ label: { className: "mx-0" } }}
                              className='p-button p-button-sm diag-save-button savesectionbtn'
                              label='Save Section Content & Image'
                              disabled={true}
                            />
                          )}
                        </React.Fragment>
                      </>
                    )
                  }
                </td>
              </tr>
            </tbody>
          </table>
          <Dialog
            className='diag1'
            visible={editContent}
            style={{ width: "1000px" }}
            onHide={() => handleCancel()}
            focusOnShow={false}
          >
            <div className='diag-box'>
              <div className='grid'>
                <div className='col-12 p-0 diag-syndication'>Previous Session Highlights</div>
                <div className='col-12 pl-0'>
                  <InputTextarea
                    required
                    rows={20}
                    className='widthFull'
                    value={temp}
                    onChange={(event) => setTemp(event.target.value)}
                  />
                </div>
                <div className='col-12 flex flex-row-reverse'>
                  <Button
                    label='Cancel'
                    className='p-button ml-2 p-button-sm diag-discard-button'
                    onClick={() => handleCancel()}
                  />
                  <Button
                    label='Save'
                    onClick={() => handleSave(section)}
                    className='p-button ml-2 p-button-sm diag-save-button'
                  />
                  {(businessUnit === BUSINESS_UNIT.COCOA && (region === REGIONS.SA_PG || region === REGIONS.SA_ES)) ||
                    regions.map((reg) => {
                      if (reg.value !== region) {
                        return (
                          <Button
                            key={reg.value}
                            label={"Copy from " + reg.value}
                            onClick={() => {
                              setEditContent(false);
                              getContentBasedonDateAndRegion(
                                reg.value,
                                "Previous Session",
                                "current",
                                section,
                                getSectionData
                              );
                            }}
                            className='p-button ml-2 p-button-sm diag-save-button'
                          ></Button>
                        );
                      }
                      return <React.Fragment key={reg.value}></React.Fragment>;
                    })}
                  <Button
                    label='Copy from Yesterday'
                    onClick={() => {
                      setEditContent(false);
                      getContentBasedonDateAndRegion(region, "Previous Session", "previous", section, getSectionData);
                    }}
                    className='p-button ml-2 p-button-sm diag-save-button'
                  />
                </div>
              </div>
            </div>
          </Dialog>
          <Dialog
            header='Previous Session Validation'
            className='section__validation__dialog'
            baseZIndex={10001}
            visible={isSectionInValid}
            modal
            style={{ width: "350px" }}
            footer={
              <Button
                label='Okay'
                icon='pi pi-check'
                onClick={() => setIsSectionInValid(false)}
                className='p-button-text'
              />
            }
            onHide={() => setIsSectionInValid(false)}
          >
            <div className='flex flex-column' style={{ color: "#FFF" }}>
              <div className='flex align-items-center'>
                <i className='pi pi-exclamation-triangle mr-3' style={{ fontSize: "2rem" }} />
                <span>Please fill the section content</span>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default PreviousSession;
